import type {
  CustomInsightsApiDashboard,
  CustomInsightsApiDashboardLayout,
  CustomInsightsApiDashboardUpsertInput,
} from "@tracify/shared";
import {
  DEFAULT_DASHBOARD_TIME_RANGE,
  type CreateDashboardInput,
  type Dashboard,
} from "@lib/types/dashboards";
import type { Layout } from "@lib/types/insights";

type NormalizedDashboardUpsertInput = Omit<
  CreateDashboardInput,
  "defaultTimeRange" | "defaultAttributionPresetId"
> & {
  defaultTimeRange: NonNullable<Dashboard["defaultTimeRange"]>;
  defaultAttributionPresetId: Exclude<
    Dashboard["defaultAttributionPresetId"],
    undefined
  >;
};

const FALLBACK_LG_LAYOUT: Omit<Layout, "i"> = {
  x: 0,
  y: 0,
  w: 3,
  h: 3,
  minW: 1,
  maxW: 6,
  minH: 1,
  maxH: 8,
};

const FALLBACK_SM_LAYOUT: Omit<Layout, "i"> = {
  x: 0,
  y: 0,
  w: 1,
  h: 3,
  minW: 1,
  maxW: 1,
  minH: 1,
  maxH: 8,
};

function toFiniteInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.floor(value);
  }
  return null;
}

function toTrimmedString(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

function toEpochMs(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.floor(value);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function toApiLayout(layout: Layout): CustomInsightsApiDashboardLayout {
  return {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
    ...(typeof layout.minW === "number" ? { min_w: layout.minW } : {}),
    ...(typeof layout.maxW === "number" ? { max_w: layout.maxW } : {}),
    ...(typeof layout.minH === "number" ? { min_h: layout.minH } : {}),
    ...(typeof layout.maxH === "number" ? { max_h: layout.maxH } : {}),
  };
}

function toLayout(
  layout: unknown,
  insightId: string,
  fallback: Omit<Layout, "i">,
  fallbackY: number
): Layout {
  const source =
    layout && typeof layout === "object" && !Array.isArray(layout)
      ? (layout as Record<string, unknown>)
      : null;
  const x = source ? toFiniteInt(source.x) : null;
  const y = source ? toFiniteInt(source.y) : null;
  const w = source ? toFiniteInt(source.w) : null;
  const h = source ? toFiniteInt(source.h) : null;

  return {
    i: insightId,
    x: x ?? fallback.x,
    y: y ?? fallbackY,
    w: w ?? fallback.w,
    h: h ?? fallback.h,
    ...(toFiniteInt(source?.min_w ?? source?.minW) !== null
      ? { minW: toFiniteInt(source?.min_w ?? source?.minW)! }
      : fallback.minW !== undefined
        ? { minW: fallback.minW }
        : {}),
    ...(toFiniteInt(source?.max_w ?? source?.maxW) !== null
      ? { maxW: toFiniteInt(source?.max_w ?? source?.maxW)! }
      : fallback.maxW !== undefined
        ? { maxW: fallback.maxW }
        : {}),
    ...(toFiniteInt(source?.min_h ?? source?.minH) !== null
      ? { minH: toFiniteInt(source?.min_h ?? source?.minH)! }
      : fallback.minH !== undefined
        ? { minH: fallback.minH }
        : {}),
    ...(toFiniteInt(source?.max_h ?? source?.maxH) !== null
      ? { maxH: toFiniteInt(source?.max_h ?? source?.maxH)! }
      : fallback.maxH !== undefined
        ? { maxH: fallback.maxH }
        : {}),
  };
}

function getFallbackY(tileOrder: number): number {
  return Math.max(0, tileOrder) * 3;
}

export function mapApiDashboardToDashboard(
  apiDashboard: CustomInsightsApiDashboard
): Dashboard {
  const sortedTiles = [...(apiDashboard.tiles ?? [])].sort((a, b) => {
    const aOrder = toFiniteInt(a.tile_order) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = toFiniteInt(b.tile_order) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  const tiles = sortedTiles
    .map((tile, index) => {
      const insightId = toTrimmedString(tile.insight_id);
      if (!insightId) {
        return null;
      }

      const fallbackY = getFallbackY(index);
      const layouts =
        tile.layouts && typeof tile.layouts === "object"
          ? (tile.layouts as Record<string, unknown>)
          : {};
      const lgSource = layouts.lg ?? Object.values(layouts)[0];
      const smSource = layouts.sm ?? layouts.lg ?? Object.values(layouts)[0];

      return {
        insightId,
        layouts: {
          lg: toLayout(lgSource, insightId, FALLBACK_LG_LAYOUT, fallbackY),
          sm: toLayout(smSource, insightId, FALLBACK_SM_LAYOUT, fallbackY),
        },
      };
    })
    .filter(
      (tile): tile is NonNullable<Dashboard["tiles"][number]> => tile !== null
    );

  const createdAt = toEpochMs(apiDashboard.created_at) ?? Date.now();
  const updatedAt = toEpochMs(apiDashboard.updated_at) ?? createdAt;

  return {
    id: apiDashboard.id,
    title: toTrimmedString(apiDashboard.title) || "Untitled Dashboard",
    description:
      typeof apiDashboard.description === "string"
        ? apiDashboard.description
        : "",
    tiles,
    schemaVersion:
      toFiniteInt(apiDashboard.schema_version) ??
      toFiniteInt((apiDashboard as Record<string, unknown>).schemaVersion) ??
      1,
    createdAt,
    updatedAt,
    defaultTimeRange: apiDashboard.default_time_range
      ? {
          preset:
            toTrimmedString(apiDashboard.default_time_range.preset) || "today",
          customDays:
            toFiniteInt(apiDashboard.default_time_range.custom_days) ?? 0,
        }
      : undefined,
    defaultAttributionPresetId:
      apiDashboard.default_attribution_preset_id === undefined
        ? undefined
        : apiDashboard.default_attribution_preset_id,
  };
}

export function mapDashboardToApiUpsertInput(
  dashboard: NormalizedDashboardUpsertInput
): CustomInsightsApiDashboardUpsertInput {
  return {
    title: dashboard.title,
    description: dashboard.description,
    tiles:
      dashboard.tiles.map((tile, index) => ({
        insight_id: tile.insightId,
        tile_order: index,
        layouts: {
          lg: toApiLayout(tile.layouts.lg),
          sm: toApiLayout(tile.layouts.sm),
        },
      })) ?? [],
    default_time_range: {
      preset: dashboard.defaultTimeRange.preset,
      ...(dashboard.defaultTimeRange.customDays !== undefined
        ? { custom_days: dashboard.defaultTimeRange.customDays }
        : {}),
    },
    default_attribution_preset_id: dashboard.defaultAttributionPresetId,
  };
}

export function normalizeDashboardForUpsertInput(
  dashboard: Pick<Dashboard, "title" | "description" | "tiles"> & {
    defaultTimeRange?: NonNullable<Dashboard["defaultTimeRange"]> | null;
    defaultAttributionPresetId?: Exclude<
      Dashboard["defaultAttributionPresetId"],
      undefined
    >;
  }
): NormalizedDashboardUpsertInput {
  return {
    title: dashboard.title,
    description: dashboard.description ?? "",
    tiles: dashboard.tiles,
    defaultTimeRange:
      dashboard.defaultTimeRange ?? DEFAULT_DASHBOARD_TIME_RANGE,
    defaultAttributionPresetId: dashboard.defaultAttributionPresetId ?? null,
  };
}
